import { useState, useEffect } from 'react';
import { getAnalysisHistory, compareAnalyses } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  GitCompare, 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  Minus, 
  ArrowRight,
  FileText,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function AnalysisComparison({ caseId, isOpen, onClose }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [analysis1, setAnalysis1] = useState(null);
  const [analysis2, setAnalysis2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    if (isOpen && caseId) {
      loadAnalyses();
    }
  }, [isOpen, caseId]);

  const loadAnalyses = async () => {
    setLoading(true);
    try {
      const res = await getAnalysisHistory(caseId);
      setAnalyses(res.data.analyses || []);
    } catch (err) {
      toast.error('Failed to load analysis history');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!analysis1 || !analysis2) {
      toast.error('Please select two analyses to compare');
      return;
    }
    if (analysis1 === analysis2) {
      toast.error('Please select different analyses');
      return;
    }

    setComparing(true);
    try {
      const res = await compareAnalyses(caseId, analysis1, analysis2);
      setComparison(res.data);
    } catch (err) {
      toast.error('Failed to compare analyses');
    } finally {
      setComparing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const getRelevanceColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-amber-600';
    return 'text-gray-600';
  };

  const getChangeIcon = (change) => {
    if (change > 0.1) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (change < -0.1) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-bronze" />
            Compare AI Analyses
          </DialogTitle>
          <DialogDescription>
            See how different case arguments affect AI exhibit identification
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        ) : analyses.length < 2 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <GitCompare className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium mb-2">Need More Analyses</h3>
              <p className="text-sm text-muted-foreground">
                Run AI analysis at least twice with different arguments to compare results.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Selection Row */}
            <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium">Analysis 1 (Baseline)</label>
                <Select value={analysis1 || ''} onValueChange={setAnalysis1}>
                  <SelectTrigger data-testid="compare-analysis-1">
                    <SelectValue placeholder="Select analysis..." />
                  </SelectTrigger>
                  <SelectContent>
                    {analyses.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(a.created_at)}</span>
                          <Badge variant="outline" className="ml-2">{a.exhibit_count} exhibits</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ArrowRight className="w-6 h-6 text-muted-foreground mb-2" />

              <div className="space-y-2">
                <label className="text-sm font-medium">Analysis 2 (Compare)</label>
                <Select value={analysis2 || ''} onValueChange={setAnalysis2}>
                  <SelectTrigger data-testid="compare-analysis-2">
                    <SelectValue placeholder="Select analysis..." />
                  </SelectTrigger>
                  <SelectContent>
                    {analyses.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(a.created_at)}</span>
                          <Badge variant="outline" className="ml-2">{a.exhibit_count} exhibits</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={handleCompare} 
              disabled={comparing || !analysis1 || !analysis2}
              className="w-full bg-bronze hover:bg-bronze-light"
              data-testid="compare-btn"
            >
              {comparing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Comparing...</>
              ) : (
                <><GitCompare className="w-4 h-4 mr-2" />Compare Analyses</>
              )}
            </Button>

            {/* Comparison Results */}
            {comparison && (
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <Card className="bg-muted/50">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold">{comparison.summary.total_exhibits_1}</p>
                        <p className="text-xs text-muted-foreground">Exhibits (Before)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-muted/50">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold">{comparison.summary.total_exhibits_2}</p>
                        <p className="text-xs text-muted-foreground">Exhibits (After)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 dark:bg-green-950/30">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">+{comparison.summary.new_exhibits}</p>
                        <p className="text-xs text-green-600">New</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 dark:bg-red-950/30">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-red-600">-{comparison.summary.removed_exhibits}</p>
                        <p className="text-xs text-red-600">Removed</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Arguments Comparison */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Arguments Compared</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4 text-sm">
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="font-medium text-xs text-muted-foreground mb-1">Analysis 1</p>
                        <p className="text-xs">{truncateText(comparison.analysis_1.arguments, 150)}</p>
                      </div>
                      <div className="p-3 bg-bronze/10 rounded-lg">
                        <p className="font-medium text-xs text-bronze mb-1">Analysis 2</p>
                        <p className="text-xs">{truncateText(comparison.analysis_2.arguments, 150)}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* New Exhibits */}
                  {comparison.new_exhibits.length > 0 && (
                    <Card className="border-green-200 dark:border-green-900">
                      <CardHeader 
                        className="pb-2 cursor-pointer" 
                        onClick={() => setExpandedSection(expandedSection === 'new' ? null : 'new')}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Plus className="w-4 h-4 text-green-500" />
                            New Exhibits ({comparison.new_exhibits.length})
                          </CardTitle>
                          {expandedSection === 'new' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                        <CardDescription className="text-xs">Documents newly identified as relevant</CardDescription>
                      </CardHeader>
                      {expandedSection === 'new' && (
                        <CardContent className="space-y-2">
                          {comparison.new_exhibits.map((ex, i) => (
                            <div key={i} className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="w-4 h-4 text-green-600" />
                                <span className="font-medium text-sm">{ex.filename}</span>
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                                  {Math.round(ex.relevance_score * 100)}%
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{ex.reasoning}</p>
                            </div>
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Removed Exhibits */}
                  {comparison.removed_exhibits.length > 0 && (
                    <Card className="border-red-200 dark:border-red-900">
                      <CardHeader 
                        className="pb-2 cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'removed' ? null : 'removed')}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Minus className="w-4 h-4 text-red-500" />
                            Removed Exhibits ({comparison.removed_exhibits.length})
                          </CardTitle>
                          {expandedSection === 'removed' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                        <CardDescription className="text-xs">Documents no longer identified as relevant</CardDescription>
                      </CardHeader>
                      {expandedSection === 'removed' && (
                        <CardContent className="space-y-2">
                          {comparison.removed_exhibits.map((ex, i) => (
                            <div key={i} className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="w-4 h-4 text-red-600" />
                                <span className="font-medium text-sm">{ex.filename}</span>
                                <Badge variant="outline" className="text-red-600">
                                  Was {Math.round(ex.relevance_score * 100)}%
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{ex.reasoning}</p>
                            </div>
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Changed Relevance */}
                  {comparison.changed_relevance.length > 0 && (
                    <Card className="border-amber-200 dark:border-amber-900">
                      <CardHeader 
                        className="pb-2 cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === 'changed' ? null : 'changed')}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-amber-500" />
                            Relevance Changed ({comparison.changed_relevance.length})
                          </CardTitle>
                          {expandedSection === 'changed' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                        <CardDescription className="text-xs">Documents with significant relevance score changes</CardDescription>
                      </CardHeader>
                      {expandedSection === 'changed' && (
                        <CardContent className="space-y-2">
                          {comparison.changed_relevance.map((ex, i) => (
                            <div key={i} className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4 text-amber-600" />
                                <span className="font-medium text-sm">{ex.filename}</span>
                                <div className="flex items-center gap-1 ml-auto">
                                  <span className={getRelevanceColor(ex.relevance_1)}>{Math.round(ex.relevance_1 * 100)}%</span>
                                  <ArrowRight className="w-4 h-4" />
                                  <span className={getRelevanceColor(ex.relevance_2)}>{Math.round(ex.relevance_2 * 100)}%</span>
                                  {getChangeIcon(ex.change)}
                                  <span className={ex.change > 0 ? 'text-green-600' : 'text-red-600'}>
                                    ({ex.change > 0 ? '+' : ''}{Math.round(ex.change * 100)}%)
                                  </span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="p-2 bg-background rounded">
                                  <p className="font-medium text-muted-foreground mb-1">Before:</p>
                                  <p>{truncateText(ex.reasoning_1, 100)}</p>
                                </div>
                                <div className="p-2 bg-background rounded">
                                  <p className="font-medium text-bronze mb-1">After:</p>
                                  <p>{truncateText(ex.reasoning_2, 100)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* No Changes */}
                  {comparison.new_exhibits.length === 0 && 
                   comparison.removed_exhibits.length === 0 && 
                   comparison.changed_relevance.length === 0 && (
                    <Card className="border-dashed">
                      <CardContent className="p-8 text-center">
                        <p className="text-muted-foreground">No significant differences found between these analyses.</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
